import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../features/auth/AuthContext';
import { config } from '../lib/config';
import {
  ADMIN_ROUTES,
  CLIENT_TYPES,
  PARTNER_QUERY_ACTIVE,
  PARTNER_QUERY_KEY,
  PERMISSIONS,
} from '../lib/constants';
import { fetchMe } from '../lib/identityRegistry';
import { BrandLogo } from '../components/BrandLogo';

export function PostLoginChooserPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout } = useAuth();
  const isPartner = searchParams.get(PARTNER_QUERY_KEY) === PARTNER_QUERY_ACTIVE;
  const [signingOut, setSigningOut] = useState(false);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
  });

  const canManageUsers = meQuery.data?.permissions.includes(PERMISSIONS.MANAGE_USERS) ?? false;
  const canManageFacilities =
    meQuery.data?.permissions.includes(PERMISSIONS.MANAGE_FACILITIES) ?? false;
  const coreReady = Boolean(config.defaultCoreUrl);
  const portalReady = Boolean(config.defaultPortalUrl);
  // Land on the highest-privilege management page the user can access.
  const managementDestination = canManageUsers
    ? ADMIN_ROUTES.USERS
    : canManageFacilities
      ? ADMIN_ROUTES.INSTANCES
      : ADMIN_ROUTES.ACCOUNT;

  useEffect(() => {
    if (!meQuery.isSuccess) return;
    if (isPartner) return;
    if (meQuery.data.client_type === CLIENT_TYPES.PARTNER) {
      navigate(`/welcome?${PARTNER_QUERY_KEY}=${PARTNER_QUERY_ACTIVE}`, { replace: true });
    }
  }, [isPartner, meQuery.data, meQuery.isSuccess, navigate]);

  async function handleSignOut() {
    setSigningOut(true);
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="center-screen">
      <div className="chooser-card">
        <BrandLogo />
        <p className="chooser-card__eyebrow">Signed in</p>
        <h1>Where would you like to go?</h1>
        <p className="chooser-card__copy">
          {isPartner
            ? 'Partner accounts use Sense Portal for account workflows.'
            : 'Choose a destination or manage users from this portal.'}
        </p>

        {meQuery.isLoading ? <p className="hint-text">Loading your profile...</p> : null}
        {meQuery.error ? (
          <div className="form-error">
            {meQuery.error instanceof Error ? meQuery.error.message : 'Failed to load profile'}
          </div>
        ) : null}

        <div className="chooser-actions">
          {!isPartner ? (
            <button
              className="primary-button"
              disabled={!coreReady}
              type="button"
              onClick={() => {
                if (coreReady) window.location.assign(config.defaultCoreUrl);
              }}
            >
              Sense Core
            </button>
          ) : null}
          {!isPartner && !coreReady ? (
            <p className="hint-text">Sense Core is coming soon.</p>
          ) : null}

          <button
            className={isPartner ? 'primary-button' : 'secondary-button'}
            disabled={!portalReady}
            type="button"
            onClick={() => {
              if (portalReady) window.location.assign(config.defaultPortalUrl);
            }}
          >
            Sense Portal
          </button>
          {!portalReady ? <p className="hint-text">Portal URL is not configured yet.</p> : null}

          <button
            className="secondary-button"
            type="button"
            onClick={() => navigate(managementDestination)}
          >
            Management Site
          </button>

          {!coreReady && !portalReady ? (
            <p className="hint-text">
              Sense Core and Portal are coming soon. Use the Management Site to administer your
              account.
            </p>
          ) : null}

          <button className="ghost-button" disabled={signingOut} type="button" onClick={handleSignOut}>
            {signingOut ? 'Signing out...' : 'Sign out'}
          </button>
        </div>
      </div>
    </div>
  );
}
